import { useState, useEffect, useCallback, useMemo, useRef } from "react";

// --- Module-level cache and state management ---
const cache = new Map();
const subscribers = new Map();
const activeQueries = new Map();

const notifySubscribers = (key) => {
    if (subscribers.has(key)) {
        subscribers.get(key).forEach((callback) => callback());
    }
};

const fetchQuery = async (key, fetchFn, options) => {
    const { onSuccess, onError } = options;
    const entry = cache.get(key);

    if (entry?.isFetching && entry?.fetchPromise) {
        return entry.fetchPromise;
    }

    if (entry) {
        entry.isFetching = true;
        if (entry.status !== "loading") {
            entry.status = "fetching";
        }
    }
    notifySubscribers(key);

    const fetchPromise = (async () => {
        try {
            if (!fetchFn) {
                console.error([useFetch]);
                throw new Error("fetchFn is undefined");
            }

            let data = await fetchFn();
            const currentEntry = cache.get(key);
            if (currentEntry) {
                currentEntry.data = data;
                currentEntry.error = null;
                currentEntry.status = "success";
                onSuccess?.(data);
            }
            return data;
        } catch (error) {
            const currentEntry = cache.get(key);
            if (currentEntry) {
                currentEntry.error = error;
                currentEntry.status = "error";
                onError?.(error);
            }
            throw error;
        } finally {
            const currentEntry = cache.get(key);
            if (currentEntry) {
                currentEntry.isFetching = false;
                currentEntry.lastFetched = Date.now();
                currentEntry.fetchPromise = null;
            }

            notifySubscribers(key);
        }
    })();

    const currentEntry = cache.get(key);
    if (currentEntry) {
        currentEntry.fetchPromise = fetchPromise;
    }
    return fetchPromise;
};

// --- Automatic Refetching on Window Focus ---
let hasWindowFocusListener = false;
const setupWindowFocusListener = () => {
    if (hasWindowFocusListener || typeof window === "undefined") return;

    const onFocusRefetch = () => {
        for (const [key, query] of activeQueries.entries()) {
            const { fetchFn, options } = query();
            if (options.refetchOnWindowFocus && options.enabled) {
                const entry = cache.get(key);
                if (entry && !entry.isFetching) {
                    // Don't refetch if already fetching
                    fetchQuery(key, fetchFn, options);
                }
            }
        }
    };

    window.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") onFocusRefetch();
    });
    window.addEventListener("focus", onFocusRefetch);

    hasWindowFocusListener = true;
};

export function useCustomQuery(rawKey, fetchFn, options = {}) {
    const {
        enabled = true,
        staleTime = 0,
        cacheTime = 5 * 60 * 1000,
        refetchOnWindowFocus = false,
        onSuccess,
        onError,
    } = options;

    const key = useMemo(
        () => (Array.isArray(rawKey) ? JSON.stringify(rawKey) : rawKey),
        [rawKey]
    );

    const queryRef = useRef();
    queryRef.current = {
        fetchFn,
        options: {
            enabled,
            staleTime,
            refetchOnWindowFocus,
            onSuccess,
            onError,
        },
    };

    // Predictive state initialization to prevent flicker.
    const [state, setState] = useState(() => {
        if (!key) return { status: "idle" };
        const cachedEntry = cache.get(key);
        if (cachedEntry) {
            return cachedEntry;
        }
        if (enabled) {
            // This query will fetch, so we start in a loading state.
            return { status: "loading", isFetching: true };
        }
        // This query won't fetch, so it's idle.
        return { status: "idle", isFetching: false };
    });

    useEffect(() => {
        if (!key || !enabled) {
            if (state.status !== "idle") {
                setState({ status: "idle", isFetching: false });
            }
            return;
        }

        // --- Subscription ---
        const callback = () => {
            const currentState = cache.get(key);
            if (currentState) {
                setState({ ...currentState });
            }
        };

        if (!subscribers.has(key)) subscribers.set(key, new Set());
        subscribers.get(key).add(callback);
        activeQueries.set(key, () => queryRef.current);

        // --- Fetching Logic ---
        let entry = cache.get(key);
        if (!entry) {
            entry = {
                status: "loading",
                isFetching: true,
                lastFetched: 0,
            };
            cache.set(key, entry);

            fetchQuery(key, queryRef.current.fetchFn, queryRef.current.options);
        } else {

            const isStale = Date.now() - entry.lastFetched > staleTime;
            if (isStale && !entry.isFetching) {
                fetchQuery(
                    key,
                    queryRef.current.fetchFn,
                    queryRef.current.options
                );
            }
        }

        // --- Cleanup ---
        return () => {
            subscribers.get(key)?.delete(callback);
            if (subscribers.get(key)?.size === 0) {
                subscribers.delete(key);
                activeQueries.delete(key);
                setTimeout(() => {
                    if (!subscribers.has(key)) {
                        cache.delete(key);
                    }
                }, cacheTime);
            }
        };
    }, [key, enabled, staleTime, cacheTime]);

    useEffect(() => {
        setupWindowFocusListener();
    }, []);

    const refetch = useCallback(() => {
        if (key && enabled) {
            const entry = cache.get(key);
            if (entry) {
                // Force refetch by ignoring stale time
                return fetchQuery(
                    key,
                    queryRef.current.fetchFn,
                    queryRef.current.options
                );
            }
        }
        return Promise.resolve();
    }, [key, enabled]);

    return {
        data: state.data,
        error: state.error,
        isError: state.status === "error",
        isLoading: state.status === "loading",
        isFetching: state.isFetching,
        isSuccess: state.status === "success",
        refetch,
    };
}
